/**
 * Record a return against a returnable outward gate pass.
 *
 * To the user this is one action. Underneath it is two calls: the return is a real
 * inward gate pass — it gets its own number, its own carrier and its own gate
 * signature, because material physically re-entering the hospital is an event in
 * its own right, not an edit of the outward document.
 *
 * Partial returns are normal (10 went out, 7 came back), so nothing here requires
 * the full quantity.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Alert, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField } from '../../components/InvField';
import { getGatePass, createGatePass, gateInGatePass } from '../../api/gatePass';
import { formatDate } from '../../utils';
import {
  isReturnable,
  isOutstanding,
  pendingReturnQty,
  isOverdue,
  daysOverdue,
} from '../../utils/gatePassEntity';
import { IPD_COLORS } from '../../theme';

function toNumberOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export default function GatePassReturnForm() {
  const history = useHistory();
  const { id } = useParams();
  const { url } = useRouteMatch();

  const base = url.replace(/\/[^/]+\/return$/, '');
  const listUrl = url.replace(/\/gate-pass\/[^/]+\/return$/, '/gate-passes');

  const [outward, setOutward] = useState(null);
  const [rows, setRows] = useState([]);
  const [carrier, setCarrier] = useState({ vehicle_no: '', carrier_name: '', carrier_contact: '' });
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // API: GET /inventory/gate-pass/{id}/
      const data = await getGatePass(id);
      setOutward(data);
      setRows((data.lines || []).map(line => ({
        line_id: line.id,
        label: line.item_name || line.item_description || 'Item',
        serial_number: line.serial_number || '',
        item_id: line.item_id || '',
        item_description: line.item_description || '',
        grn_item_id: line.grn_item_id || '',
        batch_number: line.batch_number || '',
        uom_id: line.uom_id || '',
        pending: pendingReturnQty(line),
        // Pre-filled with everything still outside — the common case is that all
        // of it came back, and a pharmacist should not retype what is already known.
        receiving: String(pendingReturnQty(line) || ''),
        remarks: '',
      })));
    } catch (e) {
      setError(e.message || 'Failed to load gate pass');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  function updateRow(i, patch) {
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  async function save() {
    const receiving = rows.filter(r => (toNumberOrNull(r.receiving) || 0) > 0);
    if (!receiving.length) {
      toast.error('Enter the quantity that came back on at least one line');
      return;
    }
    const overReturn = receiving.find(r => (toNumberOrNull(r.receiving) || 0) > r.pending);
    if (overReturn) {
      toast.error(`Cannot return more than ${overReturn.pending} of ${overReturn.label}`);
      return;
    }

    const body = {
      direction: 'INWARD',
      // The return leg inherits the outward pass's kind. Nothing posts either
      // way; this credits returned_quantity on the outward custody register.
      returnable_flag: 'RETURNABLE',
      purpose: outward.purpose,
      gate_pass_datetime: dayjs().toISOString(),
      store_id: outward.store_id || undefined,
      party_type: outward.party_type,
      party_name: outward.party_name,
      party_contact: outward.party_contact || undefined,
      linked_gate_pass_id: outward.id,
      vehicle_no: carrier.vehicle_no || undefined,
      carrier_name: carrier.carrier_name || undefined,
      carrier_contact: carrier.carrier_contact || undefined,
      remarks: remarks || undefined,
      lines: receiving.map(r => ({
        item_id: r.item_id || undefined,
        item_description: r.item_id ? undefined : (r.item_description || r.label),
        grn_item_id: r.grn_item_id || undefined,
        batch_number: r.batch_number || undefined,
        uom_id: r.uom_id || undefined,
        serial_number: r.serial_number || undefined,
        quantity: Number(r.receiving),
        linked_gate_pass_item_id: r.line_id,
        remarks: r.remarks || undefined,
      })),
    };

    setSaving(true);
    try {
      // API: POST /inventory/gate-pass/  (the inward document)
      const created = await createGatePass(body);
      // API: POST /inventory/gate-pass/{id}/gate-in/  (the physical re-entry)
      await gateInGatePass(created.id, { remarks: remarks || undefined });
      toast.success('Return recorded');
      history.push(`${base}/${id}`);
    } catch (e) {
      toast.error(e.message || 'Failed to record the return');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (error || !outward) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Gate pass not found'}</Alert>
        <InvButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
          Back
        </InvButton>
      </Box>
    );
  }

  if (!isReturnable(outward) || !isOutstanding(outward)) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {isReturnable(outward)
            ? 'Nothing is pending on this gate pass — everything has already come back or it has been closed.'
            : 'This is a non-returnable gate pass. Nothing is expected back against it.'}
        </Alert>
        <InvButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.push(`${base}/${id}`)}>
          Back to gate pass
        </InvButton>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        breadcrumb={['Inventory Management', 'Gate Pass']}
        title="Record Return"
        description={`Against ${outward.gate_pass_number} · ${outward.party_name}`}
        actions={(
          <>
            <InvButton variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push(`${base}/${id}`)}>
              Back
            </InvButton>
            <InvButton size="small" loading={saving} onClick={save}>
              Record return
            </InvButton>
          </>
        )}
      />

      {isOverdue(outward) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This was expected back on {formatDate(outward.expected_return_date)} — {daysOverdue(outward)} days late.
        </Alert>
      )}

      <SectionCard title="What came back">
        <Alert severity="info" sx={{ mb: 2 }}>
          Partial returns are fine — enter only what actually came back. The gate pass stays
          open until everything is in.
        </Alert>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 220 }}>Item</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 150 }}>Batch no. / Serial no.</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>Still out</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 130 }}>Came back</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 180 }}>Condition / remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={row.line_id || i}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{row.batch_number || row.serial_number || '—'}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>{row.pending}</Typography>
                  </TableCell>
                  <TableCell>
                    <InvTextField
                      label=""
                      value={row.receiving}
                      onChange={e => updateRow(i, { receiving: e.target.value })}
                      type="number"
                      inputProps={{ min: 0, max: row.pending, step: 0.01 }}
                      disabled={saving || row.pending <= 0}
                    />
                  </TableCell>
                  <TableCell>
                    <InvTextField
                      label=""
                      value={row.remarks}
                      onChange={e => updateRow(i, { remarks: e.target.value })}
                      placeholder="e.g. repaired, working"
                      disabled={saving}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </SectionCard>

      <SectionCard title="Who brought it back" sx={{ mt: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField
              label="Vehicle no."
              value={carrier.vehicle_no}
              onChange={e => setCarrier({ ...carrier, vehicle_no: e.target.value })}
              placeholder="MH12AB1234"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField
              label="Carrier name"
              value={carrier.carrier_name}
              onChange={e => setCarrier({ ...carrier, carrier_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField
              label="Carrier contact"
              value={carrier.carrier_contact}
              onChange={e => setCarrier({ ...carrier, carrier_contact: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <InvTextField
              label="Remarks"
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </SectionCard>
    </Box>
  );
}
